import { useAuth } from '../contexts/AuthContext';

/**
 * Shown when a user is signed in but NOT authorized for access. Copy is
 * reason-accurate: a definitive 403 (not_allowlisted) says "not approved";
 * a network/503 failure says "couldn't verify" so an approved partner is never
 * wrongly told they're rejected during an outage. The server is the real gate —
 * this screen only explains why the API is returning 403/failing.
 */
export default function NotApproved() {
  const { user, authReason, logout, retryAuth, profileLoading } = useAuth();
  const email = user?.email ?? 'this account';
  const notApproved = authReason === 'not_approved';
  const mailto =
    'mailto:hello@henoyo.ai' +
    '?subject=' + encodeURIComponent('Henoyo docs — access request') +
    '&body=' + encodeURIComponent(`Hi Henoyo team,\n\nI'd like access to the docs. I'm signed in as ${email}.\n\nThanks!`);

  return (
    <section className="home__signin" aria-live="polite">
      <h2>{notApproved ? 'Almost there — access not approved yet' : 'We couldn’t verify your access'}</h2>
      {notApproved ? (
        <>
          <p>
            You’re signed in as <strong>{email}</strong>, but this email isn’t on the
            approved list yet, so the docs stay locked.
          </p>
          <p>
            If you’re a design partner, ask your Henoyo contact to approve your email
            (or your whole company domain) — or request access below and we’ll set up a
            quick call.
          </p>
        </>
      ) : (
        <p>
          We couldn’t confirm access for <strong>{email}</strong> right now. This is
          usually temporary — please try again in a moment.
        </p>
      )}
      <div className="home__signin-actions">
        {notApproved && (
          <a href={mailto} className="home__signin-btn">
            Request access
          </a>
        )}
        <button
          type="button"
          onClick={retryAuth}
          disabled={profileLoading}
          className="home__signin-btn home__signin-btn--ghost"
        >
          {profileLoading ? 'Checking…' : 'Try again'}
        </button>
        <button
          type="button"
          onClick={logout}
          className="home__signin-btn home__signin-btn--ghost"
        >
          Sign out
        </button>
      </div>
    </section>
  );
}
